import React from 'react';
import FloatingScrollArea from '../../common/FloatingScrollArea';
import { ChevronDown, ChevronLeft, ChevronRight, ChevronUp } from 'lucide-react';
import { useAuth } from '../../../context/AuthContext';
import { getCalendar } from '../academyApi';
import { AcademyButton, compactInputBase } from '../controls';
import { addDays, formatMonthDay, todayKST } from '../periodDates';
import { Box, ErrorNotice, Spinner, ToneBadge, useAcademyVersion, useAsync, useElementWidth } from '../ui';
import { CALENDAR_KINDS, CALENDAR_TYPES, calendarState, eventLabel, monthCells, monthRange, shiftMonth, validMonth, weekCells, weekRange } from './reportModel';
import './CalendarPanel.css';

const EVENT_COLOR = {
  registered: 'text-primary-dark', re_registered: 'text-primary-dark', withdrawn: 'text-danger',
  class_joined: 'text-blue-700', class_left: 'text-gray-600', transferred: 'text-blue-700', note_issue: 'text-yellow-800',
};

function CalendarDates({ month, day, kind, view, onChange, onOpenStudent, onOpenNotes }) {
  const { getAccessToken } = useAuth();
  const version = useAcademyVersion();
  const [ref, width] = useElementWidth();
  const agendaViewportRef = React.useRef(null);
  React.useEffect(() => {
    if (agendaViewportRef.current) agendaViewportRef.current.scrollTop = 0;
  }, [day, kind]);
  const range = view === 'month' ? monthRange(month) : weekRange(day);
  const cells = view === 'month' ? monthCells(month) : weekCells(day);
  const types = kind === 'all' ? CALENDAR_TYPES : [kind];
  const requestKey = `${range.from}-${range.to}-${kind}-${version}`;
  // 요청 범위도 응답과 같이 보관한다. 기간을 바꿀 때 이전 범위의 사건을 보이지 않는다.
  const result = useAsync(async (signal) => ({ key: requestKey, data: await getCalendar({ ...range, types, signal }, getAccessToken) }), requestKey);
  const data = result.data?.key === requestKey && !result.loading ? result.data.data : null;
  const busy = result.loading || (!result.error && !data);
  const today = todayKST();
  const byDay = new Map((data?.days || []).map((item) => [item.date, item.items.filter((event) => types.includes(event.type))]));
  const items = byDay.get(day) || [];

  return (
    <div ref={ref} aria-busy={busy}>
      <div className="grid grid-cols-7 text-center text-[11px] text-gray-500" aria-hidden="true">
        {['월', '화', '수', '목', '금', '토', '일'].map((label) => <span key={label} className="py-1">{label}</span>)}
      </div>
      <div className="grid grid-cols-7 gap-px overflow-hidden rounded-xl border border-gray-200 bg-gray-200" role="group" aria-label={`${range.from} ~ ${range.to} 일정 날짜`}>
        {cells.map((date, index) => {
          if (!date) return <div key={`empty-${index}`} className="bg-white" aria-hidden="true" />;
          const inMonth = date.startsWith(month);
          const enabled = view === 'week' || inMonth;
          const events = byDay.get(date) || [];
          const counts = new Map();
          events.forEach((event) => counts.set(event.type, (counts.get(event.type) || 0) + 1));
          return (
            <button
              key={date} type="button" disabled={!enabled} aria-pressed={date === day}
              aria-current={date === today ? 'date' : undefined}
              aria-label={`${date}${date === today ? ' 오늘' : ''}${data ? ` · 일정 ${events.length}건` : ''}`}
              onClick={() => onChange({ month: date.slice(0, 7), day: date })}
              className={`flex h-12 min-w-0 flex-col gap-0.5 overflow-hidden p-1 text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary ${date === day ? 'bg-primary/10 ring-1 ring-inset ring-primary' : 'bg-white enabled:hover:bg-gray-50'} ${!enabled ? 'text-gray-300' : 'text-gray-700'}`}
            >
              <span className={`inline-flex size-5 items-center justify-center rounded-full text-xs ${date === today ? 'bg-primary font-semibold text-white' : ''}`}>{Number(date.slice(8))}</span>
              {width < 420 ? events.length > 0 && <span className="text-[10px] font-medium text-gray-600">{events.length}건</span> : <>
                <span className="flex w-full min-w-0 gap-1">
                  {[...counts].slice(0, 1).map(([type, count]) => <span key={type} className={`truncate text-[10px] leading-tight ${EVENT_COLOR[type]}`}>{eventLabel(type)} {count}</span>)}
                  {counts.size > 1 && <span className="shrink-0 text-[10px] leading-tight text-gray-500">+{events.length - [...counts][0][1]}</span>}
                </span>
              </>}
            </button>
          );
        })}
      </div>
      <div className="academy-calendar-agenda mt-1.5 flex h-32 flex-col border-t border-gray-100 pt-1.5" aria-live="polite">
        <p className="h-5 shrink-0 text-xs font-semibold text-gray-900">
          {day} 일정{data && <span className="font-normal text-gray-500"> · {items.length}건</span>}
        </p>
        <FloatingScrollArea ref={agendaViewportRef} axis="y" aria-label={`${day} 일정 목록`} className="flex-1">
          {data && items.length === 0 && <p className="py-1 text-xs text-gray-500">선택한 날짜에 일정이 없습니다.</p>}
          {busy && <Spinner label="일정 불러오는 중" compact />}
          {result.error && <ErrorNotice error={result.error} onRetry={result.reload} compact />}
          {data && items.length > 0 && (
            <ul className="academy-calendar-events grid gap-x-3">
              {items.map((event, index) => (
                <li key={`${event.type}-${event.student_id || ''}-${index}`} className="flex min-w-0 items-start gap-1.5 border-b border-gray-100 py-1 text-xs">
                  <span className="flex shrink-0 items-center gap-1">
                    <ToneBadge tone={event.type === 'withdrawn' ? 'danger' : 'muted'}>{eventLabel(event.type)}</ToneBadge>
                    {event.is_future && <ToneBadge tone="info">예정</ToneBadge>}
                  </span>
                  <div className="min-w-0 flex-1 break-words">
                    {event.student_id && onOpenStudent ? (
                      <button type="button" className="text-left text-blue-700 hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-primary" onClick={() => onOpenStudent(event.student_id, event.type === 'note_issue' ? 'notes' : 'overview')}>
                        {event.student_name ? `${event.student_name} · ` : ''}{event.title || eventLabel(event.type)}
                      </button>
                    ) : event.type === 'note_issue' && onOpenNotes ? (
                      <button type="button" className="text-left text-blue-700 hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-primary" onClick={() => onOpenNotes(event.class_id ?? event.refs?.class_id)}>{event.title || '메모 보기'}</button>
                    ) : <span className="text-gray-900">{event.title || eventLabel(event.type)}</span>}
                    {event.is_date_estimated && <p className="mt-0.5 text-gray-500">이 날짜는 이관 자료를 기준으로 합니다.</p>}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </FloatingScrollArea>
      </div>
    </div>
  );
}

export default function CalendarPanel({ filters, onChange = () => {}, onOpenStudent, onOpenNotes }) {
  const { month, day, kind, view } = calendarState(filters);
  const expanded = view === 'month';
  const week = weekRange(day);
  const canMove = (amount) => expanded
    ? month !== (amount < 0 ? '1900-01' : '9998-12')
    : validMonth(addDays(day, amount * 7).slice(0, 7));
  const move = (amount) => {
    if (!canMove(amount)) return;
    const next = expanded ? `${shiftMonth(month, amount)}-01` : addDays(day, amount * 7);
    onChange({ month: next.slice(0, 7), day: next });
  };
  return (
    <Box compact>
      <div className="mb-1 flex flex-wrap items-center gap-x-2 gap-y-1">
        <h4 className="shrink-0 text-sm font-semibold text-gray-900">캘린더</h4>
        <div className="flex items-center gap-1">
          <AcademyButton aria-label={expanded ? '이전 달' : '이전 주'} disabled={!canMove(-1)} onClick={() => move(-1)}><ChevronLeft className="size-4" /></AcademyButton>
          <span className="min-w-24 text-center tabular-nums">
            <span className="block text-xs font-semibold text-gray-900">{month.replace('-', '년 ')}월</span>
            {!expanded && <span className="block text-[11px] text-gray-500">{formatMonthDay(week.from)} ~ {formatMonthDay(week.to)}</span>}
          </span>
          <AcademyButton aria-label={expanded ? '다음 달' : '다음 주'} disabled={!canMove(1)} onClick={() => move(1)}><ChevronRight className="size-4" /></AcademyButton>
        </div>
        <div className="ml-auto flex flex-wrap items-center gap-1">
          <select aria-label="일정 종류" value={kind} onChange={(e) => onChange({ kind: e.target.value })} className={`${compactInputBase} w-24 max-w-full`}>
            {CALENDAR_KINDS.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
          </select>
          <AcademyButton onClick={() => onChange({ month: todayKST().slice(0, 7), day: todayKST() })}>오늘</AcademyButton>
          <AcademyButton icon={expanded ? ChevronUp : ChevronDown} aria-label={expanded ? '접기' : '월간 펼치기'} aria-expanded={expanded} onClick={() => onChange({ view: expanded ? 'week' : 'month' })}>
            {expanded ? '접기' : '월간'}
          </AcademyButton>
        </div>
      </div>
      <CalendarDates month={month} day={day} kind={kind} view={view} onChange={onChange} onOpenStudent={onOpenStudent} onOpenNotes={onOpenNotes} />
    </Box>
  );
}
